import { useState } from "react";
import { useTranslation } from "react-i18next";
import authService from "../authService";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import { baseRoutes } from "@base/routes/baseRoutes";
import { AxiosError } from "axios";
import { useDispatch } from "react-redux";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { getErrorMsg } from "@base/helpers/common";

const useRegister = () => {
  const dispatch = useDispatch();
  const [parmas] = useSearchParams();
  const redirectTo = parmas.get("redirectTo");
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);

  const handleError = (phoneNumber: string, error: AxiosError | any) => {
    const status = error?.response?.status;

    if (status === 409) {
      // Phone number already is registered
      // Navigate to password screen
      navigate({
        pathname: baseRoutes.login,
        // Only forward redirectTo when it actually exists, otherwise the literal
        // string "null" ends up in the URL and is used as the post-login target.
        search: createSearchParams(redirectTo ? { phone: phoneNumber, redirectTo } : { phone: phoneNumber }).toString(),
      });
      return;
    }

    // The Turnstile token was refused (wrong, expired or already spent); the caller resets the widget.
    if (status === 403) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("LoginPage.AuthenticationRetry") }));
      return;
    }

    if (status === 429) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("LoginPage.OtpLimitExceeded") }));
      return;
    }

    if (status === 423) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("LoginPage.AccountBlocked") }));
      return;
    }

    dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: getErrorMsg(error) }));
  };

  const handleRegister = async (phoneNumber: string, turnstileToken?: string) => {
    try {
      setLoading(true);
      const data = await authService.register(phoneNumber, i18n.resolvedLanguage ?? "", turnstileToken);

      if (data.status === 200) {
        // Phone number is not registered
        // Navigate to OTP Screen
        navigate({
          pathname: baseRoutes.login,
          search: createSearchParams({
            phone: phoneNumber,
            otp: "true",
          }).toString(),
        });
      }
    } catch (error: AxiosError | any) {
      handleError(phoneNumber, error);
    } finally {
      setLoading(false);
    }
  };

  return {
    isLoading,
    handleRegister,
  };
};

export default useRegister;
